import type React from 'react';
import PDFDocument from 'pdfkit/js/pdfkit.standalone';
import html2canvas from 'html2canvas';
import download from 'downloadjs';
import { PDFDocument as PDFLibDocument, ParseSpeeds } from 'pdf-lib';
import biteHeaderDark from '../../resources/img/biteHeaderDark.png';
import { countries } from 'utils/countriesWithCodes';

const PDF_BODY_FONT_SIZE_PT = 9;
const PDF_CAPTURE_FONT_SIZE = '18px';
const PDF_CAPTURE_LINE_HEIGHT = '1.45';
const PDF_CAPTURE_WIDTH_CAP = 1024;

export const tariffTicketPDF = async (
  elementRef: React.RefObject<HTMLElement>,
  name: string,
  opts: {
    theme?: any;
    quality?: number;
    aggressiveCompression?: boolean;
    headerPngDataUrl?: string;
    alignLogo?: 'left' | 'center' | 'right';
    isWhiteLabel?: boolean;
    shouldDownload?: boolean;
    emailTo?: string;
  } = {},
) => {
  if (!elementRef.current) return;

  const isWhiteLabel = opts.isWhiteLabel ?? false;
  const emailTo = opts.emailTo || 'info@bitedata.io';
  const alignLogo = opts.alignLogo || 'left';
  const disclaimerText = `Users are responsible for manually verifying product scope, effective dates, list numbers, applicable exemptions, and FTAs. BITE outputs are an unofficial opinion of the BITE Data Corporation. It is intended to assist in identifying potential tariff implications and should be used alongside a comprehensive compliance review. Reliance upon this advice does not constitute reasonable care under the Customs Modernization Act. In order to exercise the highest degree of reasonable care, BITE Data Corporation recommends that users perform a complete review, which may include application for a binding ruling from U.S. Customs and Border Protection. Should you need further assistance in this matter, please contact us at ${emailTo}, where we can direct you to additional resources for your trade compliance needs.`;

  const originalEl = elementRef.current;
  const originalRect = originalEl.getBoundingClientRect();

  const rawWidth = Math.max(
    Math.ceil(originalRect.width),
    originalEl.scrollWidth,
    originalEl.clientWidth,
    originalEl.offsetWidth,
  );

  const captureWidth = Math.min(rawWidth, PDF_CAPTURE_WIDTH_CAP);
  const captureHeight = Math.max(
    Math.ceil(originalRect.height),
    originalEl.scrollHeight,
    originalEl.clientHeight,
    originalEl.offsetHeight,
  );

  if (captureWidth <= 0 || captureHeight <= 0) {
    throw new Error(
      `tariffTicketPDF target has invalid size (${captureWidth}x${captureHeight}).`,
    );
  }

  const quality = opts.quality ?? 0.95;
  const aggressiveCompression = opts.aggressiveCompression ?? false;

  // -----------------------------
  // Helpers
  // -----------------------------
  const replaceFormControlsWithText = (root: HTMLElement) => {
    // INPUTS
    root.querySelectorAll('input').forEach((node) => {
      const input = node as HTMLInputElement;
      const text =
        input.value || input.getAttribute('value') || input.placeholder || '';
      const dollarPrefix = Array.from(input.parentElement?.children || []).find(
        (child) => child !== input && child.textContent?.trim() === '$',
      ) as HTMLElement | undefined;
      const inlineDollarPrefix =
        input.id === 'value' && input.name === 'value' && dollarPrefix;

      if (inlineDollarPrefix) {
        dollarPrefix.style.display = 'none';
      }

      const span = document.createElement('span');
      span.textContent = inlineDollarPrefix
        ? `$ ${text.replace(/^\$\s*/, '')}`
        : text;
      const cs = window.getComputedStyle(input);
      Object.assign(span.style, {
        display: 'inline-flex',
        alignItems: 'center',
        width: cs.width,
        height: cs.height,
        padding: inlineDollarPrefix ? '0' : cs.padding,
        border: cs.border,
        borderRadius: cs.borderRadius,
        font: cs.font,
        lineHeight: cs.lineHeight,
        boxSizing: 'border-box',
        backgroundColor: cs.backgroundColor,
        color: '#000',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      });
      input.replaceWith(span);
    });

    // SELECTS
    root.querySelectorAll('select').forEach((node) => {
      const sel = node as HTMLSelectElement;
      const text =
        sel.selectedOptions?.[0]?.textContent?.trim() ||
        sel.value ||
        sel.getAttribute('value') ||
        '';
      const span = document.createElement('span');
      span.textContent = text;
      const cs = window.getComputedStyle(sel);
      Object.assign(span.style, {
        display: 'inline-flex',
        alignItems: 'center',
        width: cs.width,
        height: cs.height,
        padding: cs.padding,
        border: cs.border,
        borderRadius: cs.borderRadius,
        font: cs.font,
        lineHeight: cs.lineHeight,
        boxSizing: 'border-box',
        backgroundColor: cs.backgroundColor,
        color: '#000',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      });
      sel.replaceWith(span);
    });

    // TEXTAREAS
    root.querySelectorAll('textarea').forEach((node) => {
      const ta = node as HTMLTextAreaElement;
      const text = ta.value || ta.getAttribute('value') || ta.placeholder || '';
      const div = document.createElement('div');
      div.textContent = text;
      const cs = window.getComputedStyle(ta);
      Object.assign(div.style, {
        whiteSpace: 'pre-wrap',
        width: cs.width,
        minHeight: cs.height,
        padding: cs.padding,
        border: cs.border,
        borderRadius: cs.borderRadius,
        font: cs.font,
        lineHeight: cs.lineHeight,
        boxSizing: 'border-box',
        backgroundColor: cs.backgroundColor,
        color: '#000',
      });
      ta.replaceWith(div);
    });
  };

  const compressCanvasToDataUrl = (
    canvas: HTMLCanvasElement,
    targetQuality: number,
    preferLossless = false,
  ): { dataUrl: string; format: 'JPEG' | 'PNG' } => {
    if (canvas.width <= 0 || canvas.height <= 0) {
      throw new Error(`tariffTicketPDF canvas has invalid size.`);
    }

    if (preferLossless) {
      return { dataUrl: canvas.toDataURL('image/png'), format: 'PNG' };
    }

    try {
      const jpegQuality = aggressiveCompression
        ? Math.max(0.85, targetQuality * 0.9)
        : targetQuality;
      return {
        dataUrl: canvas.toDataURL('image/jpeg', jpegQuality),
        format: 'JPEG',
      };
    } catch {
      return { dataUrl: canvas.toDataURL('image/png'), format: 'PNG' };
    }
  };

  // -----------------------------
  // Clone + force light theme
  // -----------------------------
  const clone = originalEl.cloneNode(true) as HTMLElement;
  const CAPTURE_ROOT_CLASS = 'pdf-light-capture-root';
  clone.classList.add(CAPTURE_ROOT_CLASS);

  clone.style.width = `${captureWidth}px`;
  clone.style.minHeight = `${captureHeight}px`;
  clone.style.height = 'auto';
  clone.style.maxHeight = 'none';
  clone.style.position = 'fixed';
  clone.style.left = '-100000px';
  clone.style.top = '0';
  clone.style.zIndex = '-1';
  clone.style.pointerEvents = 'none';
  clone.style.overflow = 'visible';

  const lightStyle = document.createElement('style');
  lightStyle.textContent = `
    .${CAPTURE_ROOT_CLASS}, .${CAPTURE_ROOT_CLASS} * {
      text-shadow: none !important;
      box-shadow: none !important;
      filter: none !important;
      backdrop-filter: none !important;
      transition: none !important;
      animation: none !important;
      max-height: none !important;
      overflow: visible !important;
    }
    .${CAPTURE_ROOT_CLASS} * {
      color: #000000 !important;
      border-color: #dddddd !important;
    }
    .${CAPTURE_ROOT_CLASS} *:not(img):not(svg):not(path):not(canvas):not(video) {
      background-color: #ffffff !important;
      font-size: ${PDF_CAPTURE_FONT_SIZE} !important;
      line-height: ${PDF_CAPTURE_LINE_HEIGHT} !important;
    }
    .${CAPTURE_ROOT_CLASS},
    .${CAPTURE_ROOT_CLASS} .root,
    .${CAPTURE_ROOT_CLASS} .container {
      background-color: #ffffff !important;
    }
  `;
  clone.prepend(lightStyle);

  clone.style.backgroundColor = '#ffffff';
  clone.style.color = '#000000';

  replaceFormControlsWithText(clone);
  document.body.appendChild(clone);
  await new Promise((r) => setTimeout(r, 50));

  const measuredCaptureWidth = Math.max(
    captureWidth,
    clone.scrollWidth,
    clone.clientWidth,
    clone.offsetWidth,
  );
  const finalCaptureWidth = Math.min(
    measuredCaptureWidth,
    PDF_CAPTURE_WIDTH_CAP,
  );
  const finalCaptureHeight = Math.max(
    captureHeight,
    clone.scrollHeight,
    clone.clientHeight,
    clone.offsetHeight,
  );
  clone.style.width = `${finalCaptureWidth}px`;
  clone.style.minHeight = `${finalCaptureHeight}px`;

  // -----------------------------
  // Capture
  // -----------------------------
  const captureScale = aggressiveCompression ? 2 : 3;
  let canvas: HTMLCanvasElement;

  try {
    canvas = await html2canvas(clone, {
      width: finalCaptureWidth,
      height: finalCaptureHeight,
      windowWidth: finalCaptureWidth,
      windowHeight: finalCaptureHeight,
      scale: captureScale,
      useCORS: true,
      scrollX: 0,
      scrollY: 0,
      backgroundColor: '#FFFFFF',
      logging: false,
      removeContainer: true,
      imageTimeout: 2000,
      ignoreElements: (el) => {
        if (!(el instanceof HTMLElement)) return false;
        const tag = el.tagName.toLowerCase();
        return (
          tag === 'button' ||
          tag === 'svg' ||
          el.style.display === 'none' ||
          el.style.visibility === 'hidden' ||
          el.style.opacity === '0' ||
          el.hasAttribute('aria-hidden') ||
          el.hasAttribute('data-ignore-pdf')
        );
      },
    });
  } finally {
    document.body.removeChild(clone);
  }

  if (canvas.width <= 0 || canvas.height <= 0) {
    throw new Error(`tariffTicketPDF captured empty canvas.`);
  }

  let resolvedLogoBuffer: ArrayBuffer | string | null = null;
  const initialLogo =
    opts.headerPngDataUrl ||
    (!isWhiteLabel && typeof biteHeaderDark !== 'undefined'
      ? biteHeaderDark
      : null);

  if (initialLogo) {
    try {
      if (typeof initialLogo === 'string' && initialLogo.startsWith('data:')) {
        // If it's already a base64 data URL, PDFKit accepts it as-is
        resolvedLogoBuffer = initialLogo;
      } else if (typeof initialLogo === 'string') {
        // If it is a URL path, fetch it and convert to raw binary buffer
        const res = await fetch(initialLogo);
        if (res.ok) {
          resolvedLogoBuffer = await res.arrayBuffer();
        } else {
          console.warn('Network response was not ok fetching logo.');
        }
      }
    } catch (e) {
      console.warn('Failed to pre-fetch logo for PDFKit:', e);
    }
  }

  // -----------------------------
  // Build PDF with PDFKit via streams
  // -----------------------------
  const pdfBlob = await new Promise<Blob>((resolve, reject) => {
    try {
      // Create document, manually control pages to draw white backgrounds
      const doc = new PDFDocument({
        size: 'A4',
        margin: 0,
        autoFirstPage: false,
      });
      const chunks: any[] = [];

      doc.on('data', (chunk) => chunks.push(chunk));
      doc.on('end', () => {
        resolve(new Blob(chunks, { type: 'application/pdf' }));
      });

      // PDFKit strictly utilizes Points. 1 mm = 2.83465 pt.
      const pt = 2.83465;
      const pageW = 210 * pt;
      const pageH = 297 * pt;
      const marginX = 10 * pt;
      const logoMarginX = 5 * pt;
      const topYFirstPage = 50 * pt;
      const topYOtherPages = 50 * pt;
      const bottomMargin = 12 * pt;

      const drawPageChrome = () => {
        doc.addPage();

        // Ensure background is pure white
        doc.rect(0, 0, pageW, pageH).fill('#FFFFFF');

        // Draw Header Logo using the pre-fetched buffer
        if (resolvedLogoBuffer) {
          try {
            const logoBoxWidth = 26 * pt;
            const logoBoxHeight = 22 * pt;
            let logoX = logoMarginX; // Default left
            if (alignLogo === 'center') {
              logoX = (pageW - logoBoxWidth) / 2;
            } else if (alignLogo === 'right') {
              logoX = pageW - logoMarginX - logoBoxWidth;
            }

            // `fit` scales the logo down proportionally so it never exceeds
            // the header's reserved space, regardless of its aspect ratio.
            doc.image(resolvedLogoBuffer, logoX, 5 * pt, {
              fit: [logoBoxWidth, logoBoxHeight],
            });
          } catch (e) {
            console.warn('Could not add logo to PDFKit:', e);
          }
        }

        // Header Title
        doc.fillColor('#000000').fontSize(16).font('Helvetica');
        doc.text('Tariff Transaction', 0, 30 * pt, {
          width: pageW,
          align: 'center',
        });
      };

      const usableWidthPt = pageW - marginX * 2;
      const ptPerPx = usableWidthPt / canvas.width;

      const pageContentHeightPtFirst = pageH - topYFirstPage - bottomMargin;
      const pageContentHeightPtOther = pageH - topYOtherPages - bottomMargin;

      const pageContentHeightPxFirst = Math.max(
        1,
        Math.floor(pageContentHeightPtFirst / ptPerPx),
      );
      const pageContentHeightPxOther = Math.max(
        1,
        Math.floor(pageContentHeightPtOther / ptPerPx),
      );

      let offsetYpx = 0;
      let pageIndex = 0;
      let lastContentBottomY = topYFirstPage;

      while (offsetYpx < canvas.height) {
        drawPageChrome();

        const isFirstPage = pageIndex === 0;
        const topY = isFirstPage ? topYFirstPage : topYOtherPages;
        const maxSliceHeightPx = isFirstPage
          ? pageContentHeightPxFirst
          : pageContentHeightPxOther;
        const remainingPx = canvas.height - offsetYpx;
        const proposedSliceHeightPx = Math.min(maxSliceHeightPx, remainingPx);
        const nextBreakY =
          proposedSliceHeightPx < remainingPx
            ? findBestCanvasPageBreak(
                canvas,
                offsetYpx,
                proposedSliceHeightPx,
              )
            : offsetYpx + proposedSliceHeightPx;
        const sliceHeightPx = Math.max(1, nextBreakY - offsetYpx);

        const pageCanvas = document.createElement('canvas');
        pageCanvas.width = canvas.width;
        pageCanvas.height = sliceHeightPx;

        const ctx = pageCanvas.getContext('2d');
        if (!ctx) throw new Error('Failed to create temp page canvas.');

        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(
          canvas,
          0,
          offsetYpx,
          canvas.width,
          sliceHeightPx,
          0,
          0,
          canvas.width,
          sliceHeightPx,
        );

        const sampleHeight = Math.min(sliceHeightPx, 300);
        const sampleData = ctx.getImageData(
          0,
          0,
          pageCanvas.width,
          sampleHeight,
        ).data;
        let nonWhitePixels = 0;
        for (let i = 0; i < sampleData.length; i += 4) {
          if (
            sampleData[i + 3] > 0 &&
            (sampleData[i] < 245 ||
              sampleData[i + 1] < 245 ||
              sampleData[i + 2] < 245)
          ) {
            nonWhitePixels += 1;
          }
        }

        const totalPixels = pageCanvas.width * sampleHeight;
        const nonWhiteRatio =
          totalPixels > 0 ? nonWhitePixels / totalPixels : 0;
        const preferLossless = nonWhiteRatio < 0.25;

        const { dataUrl } = compressCanvasToDataUrl(
          pageCanvas,
          quality,
          preferLossless,
        );
        const sliceHeightPt = sliceHeightPx * ptPerPx;

        // Draw captured canvas slice into PDF
        doc.image(dataUrl, marginX, topY, {
          width: usableWidthPt,
          height: sliceHeightPt,
        });

        lastContentBottomY = topY + sliceHeightPt;
        offsetYpx += sliceHeightPx;
        pageIndex += 1;
      }

      // -----------------------------
      // Footer & Disclaimer
      // -----------------------------
      // 1. Draw the footer timestamp first
      doc.fontSize(PDF_BODY_FONT_SIZE_PT).fillColor('#646464').font('Helvetica');
      doc.text(
        `Generated on: ${new Date().toLocaleString()}`,
        marginX,
        pageH - 15 * pt,
      );

      // 2. Define your constrained disclaimer width
      const disclaimerWidthPt = usableWidthPt; // leave some padding on the right as well

      // 3. Set the text properties so heightOfString calculates accurately
      doc.fontSize(PDF_BODY_FONT_SIZE_PT).font('Helvetica');

      // Calculate the exact height the wrapped text block will occupy
      const disclaimerHeight = doc.heightOfString(
        `Disclaimer: ${disclaimerText}`,
        {
          width: disclaimerWidthPt,
          lineGap: 1.5,
        },
      );

      const disclaimerStartY = lastContentBottomY + 10 * pt;

      // Define the absolute lowest point the text can go before hitting the footer
      const maxAllowedY = pageH - 20 * pt;

      // 4. Dynamic boundary check: If it overflows, push to a fresh page with chrome
      if (disclaimerStartY + disclaimerHeight > maxAllowedY) {
        drawPageChrome();
        lastContentBottomY = topYOtherPages;
      }

      // 5. Final positioning and rendering
      const disclaimerY = lastContentBottomY + 10 * pt;

      doc.fontSize(PDF_BODY_FONT_SIZE_PT).fillColor('#4a4c51');
      doc.font('Helvetica-Bold').text('Disclaimer: ', marginX, disclaimerY, {
        continued: true,
        width: disclaimerWidthPt,
        lineGap: 1.5,
      });

      doc.font('Helvetica').text(disclaimerText);

      // Signal completion
      doc.end();
    } catch (e) {
      reject(e);
    }
  });

  // -----------------------------
  // Download Logic
  // -----------------------------
  const finalName = name.endsWith('.pdf') ? name : `${name}.pdf`;

  if (opts.shouldDownload ?? true) {
    const url = URL.createObjectURL(pdfBlob);
    const a = document.createElement('a');
    a.href = url;
    a.download = finalName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 250); // cleanup with slight delay
  }

  return pdfBlob;
};

type PdfTheme = 'light' | 'dark';
type PdfImageSource = string | ArrayBuffer | null | undefined;

type CanvasPdfOptions = {
  title: string;
  name: string;
  canvas: HTMLCanvasElement;
  quality?: number;
  aggressiveCompression?: boolean;
  shouldDownload?: boolean;
  logo?: PdfImageSource;
  headerCanvas?: HTMLCanvasElement;
  alignLogo?: 'left' | 'center' | 'right';
  disclaimerText?: string;
  disclaimerTitle?: string;
  footerFontSize?: number;
  topYFirstPage?: number;
  topYOtherPages?: number;
};

const PT = 2.83465;
const A4_WIDTH = 210 * PT;
const A4_HEIGHT = 297 * PT;

const ensurePdfName = (name: string) => (name.endsWith('.pdf') ? name : `${name}.pdf`);

const downloadBlob = (blob: Blob, name: string) => {
  const url = URL.createObjectURL(blob);
  try {
    const a = document.createElement('a');
    a.href = url;
    a.download = ensurePdfName(name);
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  } finally {
    setTimeout(() => URL.revokeObjectURL(url), 250);
  }
};

const buildPdfBlob = (render: (doc: any) => void): Promise<Blob> =>
  new Promise((resolve, reject) => {
    try {
      const doc = new PDFDocument({
        size: 'A4',
        margin: 0,
        autoFirstPage: false,
        bufferPages: false,
      }) as any;
      const chunks: any[] = [];

      doc.on('data', (chunk: any) => chunks.push(chunk));
      doc.on('end', () => resolve(new Blob(chunks, { type: 'application/pdf' })));
      render(doc);
      doc.end();
    } catch (error) {
      reject(error);
    }
  });

const resolveImageSource = async (
  source: PdfImageSource,
): Promise<string | ArrayBuffer | null> => {
  if (!source) return null;
  if (typeof source !== 'string') return source;
  if (source.startsWith('data:')) return source;

  try {
    const response = await fetch(source);
    if (!response.ok) return null;
    return response.arrayBuffer();
  } catch (error) {
    console.warn('Failed to load image for PDFKit:', error);
    return null;
  }
};

const sliceCanvas = (
  source: HTMLCanvasElement,
  offsetY: number,
  height: number,
): HTMLCanvasElement => {
  const pageCanvas = document.createElement('canvas');
  pageCanvas.width = source.width;
  pageCanvas.height = height;

  const ctx = pageCanvas.getContext('2d');
  if (!ctx) throw new Error('Failed to create temp PDF canvas.');

  ctx.fillStyle = '#FFFFFF';
  ctx.fillRect(0, 0, pageCanvas.width, pageCanvas.height);
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(
    source,
    0,
    offsetY,
    source.width,
    height,
    0,
    0,
    source.width,
    height,
  );

  return pageCanvas;
};

const getCanvasRowInkRatio = (
  ctx: CanvasRenderingContext2D,
  width: number,
  y: number,
) => {
  const sidePadding = Math.floor(width * 0.03);
  const sampleX = sidePadding;
  const sampleWidth = Math.max(1, width - sidePadding * 2);
  const row = ctx.getImageData(sampleX, y, sampleWidth, 1).data;
  let inkPixels = 0;

  for (let i = 0; i < row.length; i += 4) {
    const alpha = row[i + 3];
    const red = row[i];
    const green = row[i + 1];
    const blue = row[i + 2];

    if (alpha > 0 && (red < 245 || green < 245 || blue < 245)) {
      inkPixels += 1;
    }
  }

  return inkPixels / sampleWidth;
};

const findBestCanvasPageBreak = (
  sourceCanvas: HTMLCanvasElement,
  startY: number,
  targetHeight: number,
) => {
  const proposedEndY = Math.min(sourceCanvas.height, startY + targetHeight);
  if (proposedEndY >= sourceCanvas.height) {
    return proposedEndY;
  }

  const ctx = sourceCanvas.getContext('2d');
  if (!ctx) {
    return proposedEndY;
  }

  const searchBackPx = Math.min(
    300,
    Math.max(90, Math.floor(targetHeight * 0.18)),
  );
  const minBreakY = Math.max(
    startY + Math.floor(targetHeight * 0.65),
    startY + 1,
  );
  const searchStartY = Math.max(minBreakY, proposedEndY - searchBackPx);

  let bestY = proposedEndY;
  let bestInkRatio = Number.POSITIVE_INFINITY;

  for (let y = proposedEndY - 1; y >= searchStartY; y -= 2) {
    const inkRatio = getCanvasRowInkRatio(ctx, sourceCanvas.width, y);

    if (inkRatio < bestInkRatio) {
      bestInkRatio = inkRatio;
      bestY = y;
    }

    if (inkRatio <= 0.002) {
      return y;
    }
  }

  return bestInkRatio <= 0.02 ? bestY : proposedEndY;
};

const shouldUsePngForText = (canvas: HTMLCanvasElement) => {
  const ctx = canvas.getContext('2d');
  if (!ctx) return false;

  const sampleHeight = Math.min(canvas.height, 300);
  const data = ctx.getImageData(0, 0, canvas.width, sampleHeight).data;
  let nonWhite = 0;

  for (let i = 0; i < data.length; i += 4) {
    if (
      data[i + 3] > 0 &&
      (data[i] < 245 || data[i + 1] < 245 || data[i + 2] < 245)
    ) {
      nonWhite += 1;
    }
  }

  return nonWhite / Math.max(1, canvas.width * sampleHeight) < 0.25;
};

const canvasToImage = (
  canvas: HTMLCanvasElement,
  quality: number,
  aggressiveCompression: boolean,
): string => {
  if (shouldUsePngForText(canvas)) {
    return canvas.toDataURL('image/png');
  }

  try {
    const jpegQuality = aggressiveCompression
      ? Math.max(0.72, Math.min(1, quality) * 0.9)
      : Math.min(1, quality);
    return canvas.toDataURL('image/jpeg', jpegQuality);
  } catch {
    return canvas.toDataURL('image/png');
  }
};

const drawWhiteChrome = (
  doc: any,
  title: string,
  logo: string | ArrayBuffer | null,
  alignLogo: 'left' | 'center' | 'right' = 'left',
) => {
  doc.addPage();
  doc.rect(0, 0, A4_WIDTH, A4_HEIGHT).fill('#FFFFFF');

  if (logo) {
    try {
      const logoBoxWidth = 26 * PT;
      const logoBoxHeight = 22 * PT;
      const logoMarginX = 5 * PT;
      let logoX = logoMarginX;
      if (alignLogo === 'center') logoX = (A4_WIDTH - logoBoxWidth) / 2;
      if (alignLogo === 'right') logoX = A4_WIDTH - logoMarginX - logoBoxWidth;
      // `fit` scales the logo down proportionally so it never exceeds
      // the header's reserved space, regardless of its aspect ratio.
      doc.image(logo, logoX, 5 * PT, { fit: [logoBoxWidth, logoBoxHeight] });
    } catch (error) {
      console.warn('Could not add logo to PDFKit:', error);
    }
  }

  doc.fillColor('#000000').font('Helvetica').fontSize(16);
  doc.text(title, 0, 30 * PT, { width: A4_WIDTH, align: 'center' });
};

const renderCanvasPdf = async (opts: CanvasPdfOptions): Promise<Blob> => {
  const {
    title,
    name,
    canvas,
    quality = 0.9,
    aggressiveCompression = true,
    shouldDownload = true,
    alignLogo = 'left',
    disclaimerText,
    disclaimerTitle = 'Disclaimer:',
    footerFontSize = PDF_BODY_FONT_SIZE_PT,
    topYFirstPage = 50 * PT,
    topYOtherPages = 50 * PT,
  } = opts;

  if (canvas.width <= 0 || canvas.height <= 0) {
    throw new Error(`${title} PDF captured empty canvas.`);
  }

  const logo = opts.headerCanvas
    ? opts.headerCanvas.toDataURL('image/png')
    : await resolveImageSource(opts.logo);

  const pdfBlob = await buildPdfBlob((doc) => {
    const marginX = 10 * PT;
    const bottomMargin = 12 * PT;
    const usableWidth = A4_WIDTH - marginX * 2;
    const ptPerPx = usableWidth / canvas.width;
    const firstContentPx = Math.max(
      1,
      Math.floor((A4_HEIGHT - topYFirstPage - bottomMargin) / ptPerPx),
    );
    const otherContentPx = Math.max(
      1,
      Math.floor((A4_HEIGHT - topYOtherPages - bottomMargin) / ptPerPx),
    );

    let offsetY = 0;
    let pageIndex = 0;
    let lastContentBottomY = topYFirstPage;

    while (offsetY < canvas.height) {
      drawWhiteChrome(doc, title, logo, alignLogo);
      const topY = pageIndex === 0 ? topYFirstPage : topYOtherPages;
      const maxSliceHeight = pageIndex === 0 ? firstContentPx : otherContentPx;
      const remainingPx = canvas.height - offsetY;
      const proposedSliceHeight = Math.min(maxSliceHeight, remainingPx);
      const nextBreakY =
        proposedSliceHeight < remainingPx
          ? findBestCanvasPageBreak(canvas, offsetY, proposedSliceHeight)
          : offsetY + proposedSliceHeight;
      const sliceHeight = Math.max(1, nextBreakY - offsetY);
      const pageCanvas = sliceCanvas(canvas, offsetY, sliceHeight);
      const imageData = canvasToImage(pageCanvas, quality, aggressiveCompression);
      const imageHeight = sliceHeight * ptPerPx;

      doc.image(imageData, marginX, topY, {
        width: usableWidth,
        height: imageHeight,
      });

      lastContentBottomY = topY + imageHeight;
      offsetY += sliceHeight;
      pageIndex += 1;
    }

    doc.font('Helvetica').fontSize(footerFontSize).fillColor('#646464');
    doc.text(`Generated on: ${new Date().toLocaleString()}`, marginX, A4_HEIGHT - 15 * PT);

    if (!disclaimerText) return;

    const textWidth = usableWidth;
    doc.font('Helvetica').fontSize(PDF_BODY_FONT_SIZE_PT);
    const bodyHeight = doc.heightOfString(disclaimerText, {
      width: textWidth,
      lineGap: 1.5,
    });
    let disclaimerY = lastContentBottomY + 10 * PT;

    if (disclaimerY + bodyHeight + 14 * PT > A4_HEIGHT - 20 * PT) {
      drawWhiteChrome(doc, title, logo, alignLogo);
      disclaimerY = topYFirstPage;
    }

    doc.fillColor('#4a4c51').fontSize(PDF_BODY_FONT_SIZE_PT);
    doc.font('Helvetica-Bold').text(disclaimerTitle, marginX, disclaimerY);
    doc.font('Helvetica').text(disclaimerText.replace(/^Disclaimer:\s*/i, ''), marginX, disclaimerY + 12, {
      width: textWidth,
      lineGap: 1.5,
    });
  });

  if (shouldDownload) {
    downloadBlob(pdfBlob, name);
  }

  return pdfBlob;
};

const syncFormControlValues = (sourceRoot: HTMLElement, targetRoot: HTMLElement) => {
  const sourceControls = sourceRoot.querySelectorAll('input, select, textarea');
  const targetControls = targetRoot.querySelectorAll('input, select, textarea');

  sourceControls.forEach((sourceNode, index) => {
    const targetNode = targetControls[index];
    if (!targetNode) return;

    if (sourceNode instanceof HTMLInputElement && targetNode instanceof HTMLInputElement) {
      targetNode.value = sourceNode.value;
      targetNode.setAttribute('value', sourceNode.value);
      targetNode.checked = sourceNode.checked;
    }

    if (sourceNode instanceof HTMLSelectElement && targetNode instanceof HTMLSelectElement) {
      targetNode.value = sourceNode.value;
      Array.from(targetNode.options).forEach((option) => {
        option.selected = option.value === sourceNode.value;
      });
    }

    if (sourceNode instanceof HTMLTextAreaElement && targetNode instanceof HTMLTextAreaElement) {
      targetNode.value = sourceNode.value;
      targetNode.textContent = sourceNode.value;
    }
  });
};

const replaceFormControlsWithText = (root: HTMLElement) => {
  root.querySelectorAll('input').forEach((node) => {
    const input = node as HTMLInputElement;
    const text = input.value || input.getAttribute('value') || input.placeholder || '';
    const span = document.createElement('span');
    const cs = window.getComputedStyle(input);

    span.textContent = text;
    Object.assign(span.style, {
      display: 'inline-flex',
      alignItems: 'center',
      width: cs.width,
      minHeight: cs.height,
      padding: cs.padding,
      border: cs.border,
      borderRadius: cs.borderRadius,
      font: cs.font,
      lineHeight: cs.lineHeight,
      boxSizing: 'border-box',
      backgroundColor: '#FFFFFF',
      color: '#000000',
      whiteSpace: 'pre-wrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    });

    input.replaceWith(span);
  });

  root.querySelectorAll('select').forEach((node) => {
    const select = node as HTMLSelectElement;
    const text =
      select.selectedOptions?.[0]?.textContent?.trim() ||
      select.value ||
      select.getAttribute('value') ||
      '';
    const span = document.createElement('span');
    const cs = window.getComputedStyle(select);

    span.textContent = text;
    Object.assign(span.style, {
      display: 'inline-flex',
      alignItems: 'center',
      width: cs.width,
      minHeight: cs.height,
      padding: cs.padding,
      border: cs.border,
      borderRadius: cs.borderRadius,
      font: cs.font,
      lineHeight: cs.lineHeight,
      boxSizing: 'border-box',
      backgroundColor: '#FFFFFF',
      color: '#000000',
      whiteSpace: 'pre-wrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    });

    select.replaceWith(span);
  });

  root.querySelectorAll('textarea').forEach((node) => {
    const textarea = node as HTMLTextAreaElement;
    const text = textarea.value || textarea.getAttribute('value') || textarea.placeholder || '';
    const div = document.createElement('div');
    const cs = window.getComputedStyle(textarea);

    div.textContent = text;
    Object.assign(div.style, {
      whiteSpace: 'pre-wrap',
      width: cs.width,
      minHeight: cs.height,
      padding: cs.padding,
      border: cs.border,
      borderRadius: cs.borderRadius,
      font: cs.font,
      lineHeight: cs.lineHeight,
      boxSizing: 'border-box',
      backgroundColor: '#FFFFFF',
      color: '#000000',
    });

    textarea.replaceWith(div);
  });
};

const captureLightElement = async (
  elementRef: React.RefObject<HTMLElement>,
  options: {
    className: string;
    scale?: number;
    widthCap?: number;
    padding?: string;
    fontSize?: string;
    lineHeight?: string;
    extraStyle?: string;
    delayMs?: number;
  },
): Promise<HTMLCanvasElement | undefined> => {
  if (!elementRef.current) return undefined;

  const originalEl = elementRef.current;
  const rawWidth = Math.max(
    Math.ceil(originalEl.getBoundingClientRect().width),
    originalEl.scrollWidth,
    originalEl.clientWidth,
    originalEl.offsetWidth,
  );
  const rawHeight = Math.max(
    Math.ceil(originalEl.getBoundingClientRect().height),
    originalEl.scrollHeight,
    originalEl.clientHeight,
    originalEl.offsetHeight,
  );
  const captureWidth = options.widthCap ? Math.min(rawWidth, options.widthCap) : rawWidth;

  if (captureWidth <= 0 || rawHeight <= 0) {
    throw new Error(`PDF target has invalid size (${captureWidth}x${rawHeight}).`);
  }

  const clone = originalEl.cloneNode(true) as HTMLElement;
  clone.classList.add(options.className);
  clone.style.width = `${captureWidth}px`;
  clone.style.minHeight = `${rawHeight}px`;
  clone.style.height = 'auto';
  clone.style.maxHeight = 'none';
  clone.style.position = 'fixed';
  clone.style.left = '-100000px';
  clone.style.top = '0';
  clone.style.zIndex = '-1';
  clone.style.pointerEvents = 'none';
  clone.style.overflow = 'visible';
  clone.style.backgroundColor = '#FFFFFF';
  clone.style.color = '#000000';
  clone.style.boxSizing = 'border-box';
  if (options.padding) clone.style.padding = options.padding;

  syncFormControlValues(originalEl, clone);
  replaceFormControlsWithText(clone);

  const style = document.createElement('style');
  style.textContent = `
    .${options.className},
    .${options.className} * {
      color: #000000 !important;
      border-color: #dddddd !important;
      box-shadow: none !important;
      text-shadow: none !important;
      filter: none !important;
      backdrop-filter: none !important;
      animation: none !important;
      transition: none !important;
      max-height: none !important;
      overflow: visible !important;
    }

    .${options.className} *:not(img):not(svg):not(path):not(canvas):not(video) {
      background: #FFFFFF !important;
      background-color: #FFFFFF !important;
    }

    .${options.className} table {
      width: 100% !important;
      border-collapse: collapse !important;
      border-spacing: 0 !important;
    }

    .${options.className} th {
      background: #F5F5F5 !important;
      background-color: #F5F5F5 !important;
    }

    .${options.className} *:not(img):not(svg):not(path):not(canvas):not(video) {
      font-size: ${options.fontSize ?? PDF_CAPTURE_FONT_SIZE} !important;
      line-height: ${options.lineHeight ?? PDF_CAPTURE_LINE_HEIGHT} !important;
    }
    ${options.extraStyle || ''}
  `;
  clone.prepend(style);

  document.body.appendChild(clone);
  await new Promise((resolve) => setTimeout(resolve, options.delayMs ?? 80));

  const measuredWidth = Math.max(
    captureWidth,
    clone.scrollWidth,
    clone.clientWidth,
    clone.offsetWidth,
  );
  const finalWidth = options.widthCap
    ? Math.min(measuredWidth, options.widthCap)
    : measuredWidth;
  const finalHeight = Math.max(rawHeight, clone.scrollHeight, clone.clientHeight, clone.offsetHeight);
  clone.style.width = `${finalWidth}px`;
  clone.style.minHeight = `${finalHeight}px`;

  try {
    return await html2canvas(clone, {
      width: finalWidth,
      height: finalHeight,
      windowWidth: finalWidth,
      windowHeight: finalHeight,
      scale: options.scale ?? 2,
      useCORS: true,
      scrollX: 0,
      scrollY: 0,
      backgroundColor: '#FFFFFF',
      logging: false,
      removeContainer: true,
      imageTimeout: 2000,
      ignoreElements: (el) => {
        if (!(el instanceof HTMLElement)) return false;
        const tag = el.tagName.toLowerCase();
        return (
          tag === 'button' ||
          tag === 'svg' ||
          el.style.display === 'none' ||
          el.style.visibility === 'hidden' ||
          el.style.opacity === '0' ||
          el.hasAttribute('aria-hidden') ||
          el.hasAttribute('data-ignore-pdf')
        );
      },
    });
  } finally {
    document.body.removeChild(clone);
  }
};

const drawSimpleTable = (
  doc: any,
  headers: string[],
  rows: any[][],
  startY: number,
) => {
  const marginX = 10 * PT;
  const usableWidth = A4_WIDTH - marginX * 2;
  const colWidth = usableWidth / Math.max(1, headers.length);
  const padding = 5;
  let y = startY;

  const drawHeader = () => {
    if (y > A4_HEIGHT - 45 * PT) {
      drawWhiteChrome(doc, 'BITE Report', null);
      y = 50 * PT;
    }

    doc.rect(marginX, y, usableWidth, 22).fill('#F5F5F5').stroke('#D7D7D7');
    doc.fillColor('#000000').font('Helvetica-Bold').fontSize(PDF_BODY_FONT_SIZE_PT);
    headers.forEach((header, index) => {
      doc.text(String(header ?? ''), marginX + index * colWidth + padding, y + 7, {
        width: colWidth - padding * 2,
        align: 'center',
      });
    });
    y += 22;
  };

  drawHeader();
  doc.font('Helvetica').fontSize(PDF_BODY_FONT_SIZE_PT);

  rows.forEach((row) => {
    const cellHeights = row.map((cell) =>
      doc.heightOfString(String(cell ?? ''), {
        width: colWidth - padding * 2,
        align: 'center',
      }),
    );
    const rowHeight = Math.max(22, Math.max(...cellHeights) + padding * 2);

    if (y + rowHeight > A4_HEIGHT - 25 * PT) {
      drawWhiteChrome(doc, 'BITE Report', null);
      y = 50 * PT;
      drawHeader();
    }

    doc.rect(marginX, y, usableWidth, rowHeight).fill('#FFFFFF').stroke('#D7D7D7');
    row.forEach((cell, index) => {
      doc
        .rect(marginX + index * colWidth, y, colWidth, rowHeight)
        .stroke('#D7D7D7');
      doc.fillColor('#000000').font('Helvetica').fontSize(PDF_BODY_FONT_SIZE_PT);
      doc.text(String(cell ?? ''), marginX + index * colWidth + padding, y + padding, {
        width: colWidth - padding * 2,
        align: 'center',
      });
    });

    y += rowHeight;
  });
};

const buildSearchLikeReport = async (
  title: string,
  ref: any,
  heading: string,
  details: Array<[string, string]>,
  headers: string[],
  rows: any[][],
  print: boolean,
) => {
  let captureData: string | null = null;

  if (ref?.current) {
    try {
      const canvas = await html2canvas(ref.current, {
        logging: false,
        useCORS: true,
        backgroundColor: '#FFFFFF',
      });
      captureData = canvas.toDataURL('image/png');
    } catch (error) {
      console.warn('Failed to capture report reference for PDF:', error);
    }
  }

  const pdfBlob = await buildPdfBlob((doc) => {
    drawWhiteChrome(doc, heading, null);

    if (captureData) {
      try {
        doc.image(captureData, 140 * PT, 40 * PT, { width: 60 * PT });
      } catch (error) {
        console.warn('Failed to add report reference image:', error);
      }
    }

    doc.fillColor('#000000').font('Helvetica-Bold').fontSize(PDF_BODY_FONT_SIZE_PT);
    doc.text(details[0]?.[0] || 'Details:', 10 * PT, 50 * PT);

    doc.font('Helvetica').fontSize(PDF_BODY_FONT_SIZE_PT);
    details.slice(1).forEach(([label, value], index) => {
      doc.text(`${label}: ${value || ''}`, 20 * PT, (45 + index * 5) * PT);
    });

    doc.font('Helvetica-Bold').fontSize(PDF_BODY_FONT_SIZE_PT);
    doc.text('Results Details:', 0, 130 * PT, {
      width: A4_WIDTH,
      align: 'center',
    });
    drawSimpleTable(doc, headers, rows, 140 * PT);
  });

  if (print) {
    downloadBlob(pdfBlob, title);
    return undefined;
  }

  return pdfBlob;
};

export const searchReport = (
  title: string,
  ref: any,
  query: any,
  headers: string[],
  data: any,
  print: boolean,
  module: string,
): any => {
  const rows = data.map((row: any) => {
    if (module === 'bite-score') {
      return [row.field, row.module, row.dataSet, row.name];
    }

    const hitCauseKey = Object.keys(row.hitCause || {})[0];
    return [
      row.count,
      row.scoreField,
      row.module,
      row.dataset,
      hitCauseKey ? `${hitCauseKey}: ${row.hitCause[hitCauseKey]?.[0] || ''}` : '',
    ];
  });

  return buildSearchLikeReport(
    title,
    ref,
    'BITE Search Report',
    [
      ['Entity Details:', ''],
      ['Primary Search', query.primary || ''],
      ['Secondary Search', query.secondary || ''],
      ['City', query.city || ''],
      ['State', query.state || ''],
      ['Postal Code', query.postalCode || ''],
      ['Country', query.country || ''],
      ['Commodity Details:', ''],
      ['Commodity Name', query.commodity_name || ''],
      ['HS Code', query.hsCodeSearch || ''],
    ],
    headers,
    rows,
    print,
  );
};

export const bulkReport = (
  title: string,
  ref: any,
  headers: string[],
  data: any,
  print: boolean,
): any => {
  const rows = data.map((row: any) => [
    row.field,
    row.column,
    row.dataSet,
    row.row,
    row.hits,
  ]);

  return buildSearchLikeReport(
    title,
    ref,
    'BITE Bulk Report',
    [
      ['Search Details:', ''],
      ['Transactions Searched Against', String(data.length)],
    ],
    headers,
    rows,
    print,
  );
};

export const classificationTicketPDF = async (
  elementRef: React.RefObject<HTMLElement>,
  name: string,
  opts: {
    theme?: PdfTheme;
    quality?: number;
    aggressiveCompression?: boolean;
    shouldDownload?: boolean;
  } = {},
) => {
  const canvas = await captureLightElement(elementRef, {
    className: 'pdf-classification-light-root',
    scale: opts.aggressiveCompression ?? true ? 1.2 : 1.5,
    widthCap: PDF_CAPTURE_WIDTH_CAP,
    padding: '20px',
    fontSize: PDF_CAPTURE_FONT_SIZE,
    lineHeight: PDF_CAPTURE_LINE_HEIGHT,
    delayMs: 200,
    extraStyle: `
      .pdf-classification-light-root .explanatory_notes-classify,
      .pdf-classification-light-root .explanatory_notes-classify.expanded,
      .pdf-classification-light-root .class-ticket-cross-expanded,
      .pdf-classification-light-root .class-ticket-cross-expandeds,
      .pdf-classification-light-root .class-ticket-cross-rules,
      .pdf-classification-light-root .classify-body-ticket,
      .pdf-classification-light-root .api-container,
      .pdf-classification-light-root .api-container-no-height,
      .pdf-classification-light-root .api-container-left,
      .pdf-classification-light-root .api-container-right {
        display: block !important;
        -webkit-line-clamp: unset !important;
        -webkit-box-orient: unset !important;
        overflow: visible !important;
        max-height: none !important;
        height: auto !important;
        min-height: 0 !important;
        white-space: normal !important;
        text-overflow: unset !important;
      }

      .pdf-classification-light-root .expl-notes-btn,
      .pdf-classification-light-root .expl-notes-btn-classify {
        display: none !important;
      }
    `,
  });

  if (!canvas) return;

  return renderCanvasPdf({
    title: 'Classification Ticket',
    name: name,
    canvas: canvas,
    quality: opts.quality ?? 0.92,
    aggressiveCompression: opts.aggressiveCompression ?? true,
    shouldDownload: opts.shouldDownload ?? true,
    logo: biteHeaderDark,
    disclaimerText:
      'The 10-digit HTS classification provided is for general, educational and planning purposes only and not for purposes of filing and entry. Unless you are the importer of record, the specific tariff classification to be applied to a specific entry of merchandise is to be determined, supervised and controlled by the licensed customs broker. Users other than the importer of record must consult a licensed customs broker as classification for Customs entry purposes can only be determined by a licensed customs broker.',
  });
};

export const productTicketPDF = async (
  elementRef: React.RefObject<HTMLElement>,
  name: string,
  product: string,
  opts: {
    theme?: PdfTheme;
    quality?: number;
    aggressiveCompression?: boolean;
    headerPngDataUrl?: string;
    headerRef?: React.RefObject<HTMLElement>;
  } = {},
) => {
  let headerCanvas: HTMLCanvasElement | undefined;

  if (opts.headerRef?.current) {
    try {
      headerCanvas = await html2canvas(opts.headerRef.current, {
        useCORS: true,
        scrollX: 0,
        scrollY: 0,
        backgroundColor: '#FFFFFF',
        logging: false,
        removeContainer: true,
        imageTimeout: 2000,
      });
    } catch (error) {
      console.warn('Failed to capture product PDF header:', error);
    }
  }

  const canvas = await captureLightElement(elementRef, {
    className: 'pdf-product-light-root',
    scale: opts.aggressiveCompression ?? true ? 2 : 3,
    widthCap: PDF_CAPTURE_WIDTH_CAP,
    delayMs: 80,
    extraStyle: `
      .pdf-product-light-root * {
        border-color: #dddddd !important;
      }
    `,
  });

  if (!canvas) return null;

  return renderCanvasPdf({
    title: product,
    name: name,
    canvas: canvas,
    quality: opts.quality ?? 0.85,
    aggressiveCompression: opts.aggressiveCompression ?? true,
    shouldDownload: true,
    logo: opts.headerPngDataUrl || biteHeaderDark,
    headerCanvas: headerCanvas,
  });
};

export const screeningTicketPDF = async (
  elementRef: React.RefObject<HTMLElement>,
  name: string,
  opts: {
    theme?: PdfTheme;
    quality?: number;
    aggressiveCompression?: boolean;
    shouldDownload?: boolean;
  } = {},
) => {
  const canvas = await captureLightElement(elementRef, {
    className: 'pdf-screening-light-root',
    scale: opts.aggressiveCompression ?? true ? 2 : 3,
    widthCap: PDF_CAPTURE_WIDTH_CAP,
    padding: '20px',
    fontSize: PDF_CAPTURE_FONT_SIZE,
    lineHeight: PDF_CAPTURE_LINE_HEIGHT,
    delayMs: 150,
    extraStyle: `
      .pdf-screening-light-root .monitor-results-footer,
      .pdf-screening-light-root .screening-results-footer-action-group,
      .pdf-screening-light-root .screening-results-pagination-text,
      .pdf-screening-light-root .hb-pagination {
        background: #FFFFFF !important;
        background-color: #FFFFFF !important;
        color: #000000 !important;
        border-color: #B8B8B8 !important;
      }

      .pdf-screening-light-root .view-history-body-frame-row,
      .pdf-screening-light-root .view-history-body-frame-row-date,
      .pdf-screening-light-root .view-history-body-frame-row-dates,
      .pdf-screening-light-root .view-history-body-frame-row-name {
        background: #FFFFFF !important;
        background-color: #FFFFFF !important;
        border-color: #B8B8B8 !important;
      }
    `,
  });

  if (!canvas) return;

  return renderCanvasPdf({
    title: 'Screening Ticket',
    name: name,
    canvas: canvas,
    quality: opts.quality ?? 0.85,
    aggressiveCompression: opts.aggressiveCompression ?? true,
    shouldDownload: opts.shouldDownload ?? true,
    logo: biteHeaderDark,
  });
};

export const importUnifiedTicketPDF = async (
  elementRef: React.RefObject<HTMLElement>,
  name: string,
  opts: {
    theme?: PdfTheme;
    quality?: number;
    aggressiveCompression?: boolean;
    shouldDownload?: boolean;
    title?: string;
    captureFontSize?: string;
    captureLineHeight?: string;
    disclaimerText?: string;
    disclaimerTitle?: string;
  } = {},
) => {
  const canvas = await captureLightElement(elementRef, {
    className: 'pdf-import-unified-capture-root',
    scale: opts.aggressiveCompression ?? true ? 2 : 3,
    widthCap: PDF_CAPTURE_WIDTH_CAP,
    padding: '20px',
    fontSize: opts.captureFontSize ?? PDF_CAPTURE_FONT_SIZE,
    lineHeight: opts.captureLineHeight ?? PDF_CAPTURE_LINE_HEIGHT,
    delayMs: 200,
    extraStyle: `
      .pdf-import-unified-capture-root .results-card-frame,
      .pdf-import-unified-capture-root .results-card-frame-comp,
      .pdf-import-unified-capture-root .results-frame,
      .pdf-import-unified-capture-root .results-frame-col,
      .pdf-import-unified-capture-root .entries-frame-body-cont-lenses,
      .pdf-import-unified-capture-root .api-container,
      .pdf-import-unified-capture-root .api-container-left {
        width: 100% !important;
        max-width: none !important;
        overflow: visible !important;
      }

      .pdf-import-unified-capture-root .input-icon-container {
        display: none !important;
      }

      .pdf-import-unified-capture-root .tabs-filters-input {
        padding-left: 14px !important;
      }

      .pdf-import-unified-capture-root .explanatory_notes-classify,
      .pdf-import-unified-capture-root .explanatory_notes-classify.expanded,
      .pdf-import-unified-capture-root .class-ticket-cross-expanded,
      .pdf-import-unified-capture-root .class-ticket-cross-expandeds,
      .pdf-import-unified-capture-root .class-ticket-cross-rules,
      .pdf-import-unified-capture-root .classify-body-ticket,
      .pdf-import-unified-capture-root .api-container-no-height,
      .pdf-import-unified-capture-root .api-container-right {
        display: block !important;
        -webkit-line-clamp: unset !important;
        -webkit-box-orient: unset !important;
        overflow: visible !important;
        max-height: none !important;
        height: auto !important;
        min-height: 0 !important;
        white-space: normal !important;
        text-overflow: unset !important;
      }

      .pdf-import-unified-capture-root .expl-notes-btn,
      .pdf-import-unified-capture-root .expl-notes-btn-classify {
        display: none !important;
      }

      .pdf-import-unified-capture-root .entity-results-table-card,
      .pdf-import-unified-capture-root .entity-results-table-scroll {
        width: 100% !important;
        max-width: 100% !important;
        overflow: hidden !important;
      }

      .pdf-import-unified-capture-root .entity-results-table {
        width: 100% !important;
        max-width: 100% !important;
        table-layout: fixed !important;
        border-collapse: collapse !important;
      }

      .pdf-import-unified-capture-root .entity-results-table th,
      .pdf-import-unified-capture-root .entity-results-table td {
        box-sizing: border-box !important;
        overflow: hidden !important;
        text-overflow: ellipsis !important;
        white-space: nowrap !important;
        padding: 10px 12px !important;
        font-size: 14px !important;
        line-height: 1.3 !important;
        vertical-align: middle !important;
      }

      .pdf-import-unified-capture-root .entity-results-table th:nth-child(1),
      .pdf-import-unified-capture-root .entity-results-table td:nth-child(1) {
        width: 16% !important;
      }

      .pdf-import-unified-capture-root .entity-results-table th:nth-child(2),
      .pdf-import-unified-capture-root .entity-results-table td:nth-child(2) {
        width: 16% !important;
      }

      .pdf-import-unified-capture-root .entity-results-table th:nth-child(3),
      .pdf-import-unified-capture-root .entity-results-table td:nth-child(3) {
        width: 15% !important;
      }

      .pdf-import-unified-capture-root .entity-results-table th:nth-child(4),
      .pdf-import-unified-capture-root .entity-results-table td:nth-child(4) {
        width: 9% !important;
        text-align: center !important;
      }

      .pdf-import-unified-capture-root .entity-results-table th:nth-child(5),
      .pdf-import-unified-capture-root .entity-results-table td:nth-child(5) {
        width: 28% !important;
      }

      .pdf-import-unified-capture-root .entity-results-table th:nth-child(6),
      .pdf-import-unified-capture-root .entity-results-table td:nth-child(6) {
        width: 16% !important;
      }

      .pdf-import-unified-capture-root .entity-lens-badge {
        min-width: 0 !important;
        max-width: 100% !important;
        padding: 2px 8px !important;
        font-size: 13px !important;
        line-height: 1.2 !important;
      }
    `,
  });

  if (!canvas) return;

  return renderCanvasPdf({
    title: opts.title ?? 'Import Unified Ticket',
    name: name,
    canvas: canvas,
    quality: opts.quality ?? 0.85,
    aggressiveCompression: opts.aggressiveCompression ?? true,
    shouldDownload: opts.shouldDownload ?? true,
    logo: biteHeaderDark,
    disclaimerText: opts.disclaimerText,
    disclaimerTitle: opts.disclaimerTitle ?? 'Disclaimer:',
    footerFontSize: PDF_BODY_FONT_SIZE_PT,
  });
};

export const eccnTicketPDF = async (
  elementRef: React.RefObject<HTMLElement>,
  name: string,
  opts: {
    theme?: PdfTheme;
    quality?: number;
    aggressiveCompression?: boolean;
    shouldDownload?: boolean;
  } = {},
) => {
  return importUnifiedTicketPDF(elementRef, name, {
    ...opts,
    title: 'ECCN Results',
    captureFontSize: PDF_CAPTURE_FONT_SIZE,
    captureLineHeight: PDF_CAPTURE_LINE_HEIGHT,
    disclaimerText:
      'This tool provides automated determinations of potential export controls based on ECCN and country inputs. While efforts are made to reflect applicable regulations, no guarantee is made as to the accuracy, completeness, or current applicability of the results.\n\nExport control requirements may vary based on additional factors not captured by this tool, including end use, end user, and specific transaction details. Users are solely responsible for validating all outputs and ensuring compliance with applicable export control laws and regulations, including the U.S. Export Administration Regulations (EAR).',
    disclaimerTitle: 'Disclaimer:',
  });
};

export const screeningTicketPDFS = async (
  elementRef: React.RefObject<HTMLElement>,
  name: string,
  _opts: { theme?: PdfTheme } = {},
) => {
  const canvas = await captureLightElement(elementRef, {
    className: 'pdf-screening-simple-light-root',
    scale: 2,
    widthCap: PDF_CAPTURE_WIDTH_CAP,
    delayMs: 150,
  });

  if (!canvas) return;

  return renderCanvasPdf({
    title: 'Screening',
    name: name,
    canvas: canvas,
    quality: 0.9,
    aggressiveCompression: false,
    shouldDownload: true,
    logo: biteHeaderDark,
  });
};

type CbpLineFieldTemplate = {
  lineNumber: string;
  htsusNumber: string;
  materialPercent: string;
  enteredValue: string;
  htsusRate: string;
  dutyAmount: string;
  otherFees: string;
};

type Cbp7501FieldMap = {
  transactionNumber: string;
  summaryDate: string;
  entryDate: string;
  modeOfTransport: string;
  countryOfOrigin: string;
  exportDate: string;
  exportingCountry: string;
  countryOfMeltAndPour: string;
  primaryCountryOfSmelt: string;
  totalEnteredValue: string;
  totalDuty: string;
  totalOtherFees: string;
  totalAmountDue: string;
};

type Cbp7501Options = {
  templateUrl?: string;
  flatten?: boolean;
  fieldMap?: Partial<Cbp7501FieldMap>;
  lineFieldMap?: Partial<CbpLineFieldTemplate>;
  maxLineRows?: number;
};

const DEFAULT_CBP_7501_FIELD_MAP: Cbp7501FieldMap = {
  transactionNumber: 'Form[0].P1[0].refno[0]',
  summaryDate: 'Form[0].P1[0].summaryDate[0]',
  entryDate: 'Form[0].P1[0].entryDate[0]',
  modeOfTransport: 'Form[0].P1[0].mode[0]',
  countryOfOrigin: 'Form[0].P1[0].BirthCountry[0]',
  exportDate: 'Form[0].P1[0].exportDate[0]',
  exportingCountry: 'Form[0].P1[0].ExportCountry[0]',
  countryOfMeltAndPour: 'Form[0].P1[0].OMP[0]',
  primaryCountryOfSmelt: 'Form[0].P1[0].PCS[0]',
  totalEnteredValue: 'Form[0].P1[0].total35[0]',
  totalDuty: 'Form[0].P1[0].duty37[0]',
  totalOtherFees: 'Form[0].P1[0].totalotherfees[0]',
  totalAmountDue: 'Form[0].P1[0].total40[0]',
};

const DEFAULT_CBP_7501_LINE_FIELDS: CbpLineFieldTemplate = {
  lineNumber: 'Form[0].P1[0].lineno1[{line_index}]',
  htsusNumber: 'Form[0].P1[0].descriptiona1[{line_index}]',
  materialPercent: 'Form[0].P1[0].descriptionb1[{line_index}]',
  enteredValue: 'Form[0].P1[0].amount1[{line_index}]',
  htsusRate: 'Form[0].P1[0].descriptionc1[{line_index}]',
  dutyAmount: '',
  otherFees: '',
};

const DEFAULT_CBP_7501_LINE_FIELDS2: CbpLineFieldTemplate = {
  lineNumber: 'Form[0].P2[0].lineno1[{line_index}]',
  htsusNumber: 'Form[0].P2[0].descriptiona1[{line_index}]',
  materialPercent: 'Form[0].P2[0].descriptionb1[{line_index}]',
  enteredValue: 'Form[0].P2[0].amount1[{line_index}]',
  htsusRate: 'Form[0].P2[0].descriptionc1[{line_index}]',
  dutyAmount: '',
  otherFees: '',
};

const cbpResolveDate = (value: any): string => {
  const raw = value?.$date ?? value;
  if (!raw) return '';
  const date = raw instanceof Date ? raw : new Date(raw);
  if (Number.isNaN(date.getTime())) return '';
  return date.toISOString().slice(0, 10);
};

const cbpToNumber = (value: any): number => {
  const num = Number(value);
  return Number.isFinite(num) ? num : 0;
};

const cbpMoney = (value: any): string => cbpToNumber(value).toFixed(2);

const cbpRateToDecimal = (rate: string): number => {
  const raw = String(rate ?? '').toLowerCase();
  if (!raw || raw.includes('free')) return 0;
  const parsed = parseFloat(raw.replace(/[^0-9.]/g, ''));
  if (Number.isNaN(parsed)) return 0;
  return parsed > 1 ? parsed / 100 : parsed;
};

const cbpLineField = (pattern: string, lineNumber: number): string =>
  pattern
    .replace('{line}', String(lineNumber))
    .replace('{line_index}', String(Math.max(0, lineNumber - 1)));

const cbpGetHts33FieldOrder = (form: any): string[] => {
  const rows = form
    .getFields()
    .map((field: any) => {
      const name = field.getName();
      if (!/\.descriptiona1\[\d+\]$/.test(name)) return null;
      const widget = field.acroField?.getWidgets?.()[0];
      if (!widget) return null;
      const rect = widget.getRectangle();
      return {
        name: name,
        page: name.includes('.P2[') ? 2 : 1,
        x: Number(rect?.x || 0),
        y: Number(rect?.y || 0),
      };
    })
    .filter(Boolean)
    .filter((row: any) => row.x <= 120);

  rows.sort((a: any, b: any) => a.page - b.page || b.y - a.y || a.x - b.x);
  return rows.map((row: any) => row.name);
};

const cbpExtractLineRows = (caseData: any) => {
  const tariffResults = caseData?.tariffResults ?? {};
  const results = tariffResults?.results ?? {};
  const math = tariffResults?.math ?? {};
  const inputs = tariffResults?.inputs ?? {};
  const fallbackHts =
    caseData?.commodity?.harmonizedScheduleCode || inputs?.htsCode || '';
  const lineValueFallback = cbpToNumber(inputs?.productValue);

  const lines =
    Array.isArray(results?.lines) && results.lines.length > 0
      ? results.lines
      : [
          {
            value: lineValueFallback,
            rows: Array.isArray(results?.summaryRows)
              ? results.summaryRows
              : [],
          },
        ];

  const lineRows: Array<{
    lineNumber: string;
    htsusNumber: string;
    materialPercent: string;
    enteredValue: string;
    htsusRate: string;
    dutyAmount: string;
    otherFees: string;
  }> = [];

  const totalOtherFees = cbpToNumber(math?.mpf) + cbpToNumber(math?.hmf);

  lines.forEach((line: any, lineIndex: number) => {
    const enteredValue = cbpToNumber(line?.value ?? lineValueFallback);
    const duties =
      Array.isArray(line?.rows) && line.rows.length > 0
        ? line.rows
        : [{ code: fallbackHts, rate: '0%' }];

    duties.forEach((duty: any, dutyIndex: number) => {
      const lineNo = lineIndex + 1;
      const rate = String(duty?.rate ?? '');
      const dutyAmount = enteredValue * cbpRateToDecimal(rate);
      const otherFees = lineIndex === 0 && dutyIndex === 0 ? totalOtherFees : 0;

      lineRows.push({
        lineNumber: String(lineNo),
        htsusNumber: String(duty?.code || fallbackHts),
        materialPercent: cbpMoney(enteredValue),
        enteredValue: cbpMoney(dutyAmount),
        htsusRate: rate,
        dutyAmount: cbpMoney(dutyAmount),
        otherFees: otherFees > 0 ? cbpMoney(otherFees) : '',
      });
    });
  });

  return lineRows;
};

const cbpExtractOrderedHtsCodes = (caseData: any): string[] => {
  const lines = caseData?.tariffResults?.results?.lines;
  if (Array.isArray(lines) && lines.length > 0) {
    return lines
      .flatMap((line: any) => (Array.isArray(line?.rows) ? line.rows : []))
      .map((row: any) => String(row?.code || '').trim())
      .filter((code: string) => code.length > 0);
  }

  return cbpExtractLineRows(caseData)
    .map((row) => String(row?.htsusNumber || '').trim())
    .filter((code) => code.length > 0);
};

export const cbp7501EntrySummaryPDF = async (
  caseData: any,
  fileName: string,
  opts: Cbp7501Options = {},
): Promise<void> => {
  const templateUrl = opts.templateUrl || '/templates/CBP_Form_7501.pdf';
  const controller = new AbortController();
  const timeoutId = window.setTimeout(() => controller.abort(), 15000);

  let response: Response;
  try {
    response = await fetch(templateUrl, { signal: controller.signal });
  } finally {
    window.clearTimeout(timeoutId);
  }

  if (!response.ok) {
    throw new Error(
      `CBP template not found at "${templateUrl}". Put the fillable PDF in client/public/templates.`,
    );
  }

  const templateBytes = await response.arrayBuffer();
  const pdfDoc = await PDFLibDocument.load(templateBytes, {
    parseSpeed: ParseSpeeds.Fastest,
  });
  const form = pdfDoc.getForm();

  const setFieldText = (fieldName: string, value: string) => {
    if (!fieldName || value === undefined || value === null) return;
    const stringValue = String(value).trim();
    if (!stringValue) return;

    try {
      form.getTextField(fieldName).setText(stringValue);
      return;
    } catch {}

    try {
      const dropdown = form.getDropdown(fieldName);
      const options = dropdown.getOptions();
      const lower = stringValue.toLowerCase();
      const matched =
        options.find((option: string) => option.toLowerCase() === lower) ||
        options.find((option: string) => option.toLowerCase().includes(lower));
      dropdown.select(matched || stringValue);
    } catch {}
  };

  const clearFieldText = (fieldName: string) => {
    if (!fieldName) return;
    try {
      form.getTextField(fieldName).setText('');
    } catch {}
  };

  const ticket = caseData ?? {};
  const tariffResults = ticket?.tariffResults ?? {};
  const inputs = tariffResults?.inputs ?? {};
  const math = tariffResults?.math ?? {};
  let smeltCountry = '';
  let pourCountry = '';

  if (tariffResults?.countryUi?.key === 'SMELTCOUNTRY') {
    smeltCountry =
      countries.find(
        (country) =>
          country.iso2.toUpperCase() === String(inputs.loadingCountry || '').toUpperCase(),
      )?.name || '';
  } else if (tariffResults?.countryUi?.key === 'POURCOUNTRY') {
    pourCountry =
      countries.find(
        (country) =>
          country.iso2.toUpperCase() === String(inputs.loadingCountry || '').toUpperCase(),
      )?.name || '';
  }

  const mapped = {
    transactionNumber:
      ticket?.transactionNumber ||
      ticket?.entryNumber ||
      ticket?._id?.$oid ||
      '',
    summaryDate: cbpResolveDate(new Date()),
    entryDate: cbpResolveDate(inputs?.effectDate || ticket?.createdAt),
    modeOfTransport: ticket?.trans_method || inputs?.transportMethod || 'Ocean',
    countryOfOrigin:
      ticket?.commodity?.countryOfOrigin || inputs?.originCountry || '',
    exportDate: cbpResolveDate(inputs?.loadDate || ticket?.createdAt),
    exportCountry: '',
    countryOfMeltAndPour: pourCountry,
    primaryCountryOfSmelt: smeltCountry,
    totalEnteredValue: cbpMoney(inputs?.productValue),
    totalDuty: cbpMoney(math?.dutyCost),
    totalOtherFees: cbpMoney(cbpToNumber(math?.mpf) + cbpToNumber(math?.hmf)),
    totalAmountDue: cbpMoney(math?.totalCost),
  };

  const fieldMap = {
    ...DEFAULT_CBP_7501_FIELD_MAP,
    ...(opts.fieldMap ?? {}),
  };

  setFieldText(fieldMap.transactionNumber, mapped.transactionNumber);
  setFieldText(fieldMap.summaryDate, mapped.summaryDate);
  setFieldText(fieldMap.entryDate, mapped.entryDate);
  setFieldText(fieldMap.modeOfTransport, mapped.modeOfTransport);
  setFieldText(fieldMap.countryOfOrigin, mapped.countryOfOrigin);
  setFieldText(fieldMap.exportDate, mapped.exportDate);
  setFieldText(fieldMap.exportingCountry, mapped.exportCountry);
  setFieldText(fieldMap.countryOfMeltAndPour, mapped.countryOfMeltAndPour);
  setFieldText(fieldMap.primaryCountryOfSmelt, mapped.primaryCountryOfSmelt);
  setFieldText(fieldMap.totalEnteredValue, mapped.totalEnteredValue);
  setFieldText(fieldMap.totalDuty, mapped.totalDuty);
  setFieldText(fieldMap.totalOtherFees, mapped.totalOtherFees);
  setFieldText(fieldMap.totalAmountDue, mapped.totalAmountDue);
  setFieldText(
    'Form[0].P1[0].other[0]',
    `MPF       $${cbpMoney(cbpToNumber(math?.mpf))} \nHMF      $${cbpToNumber(
      math?.hmf,
    )}`,
  );

  const lineFieldMap = {
    ...DEFAULT_CBP_7501_LINE_FIELDS,
    ...(opts.lineFieldMap ?? {}),
  };
  const lineFieldMap2 = {
    ...DEFAULT_CBP_7501_LINE_FIELDS2,
    ...(opts.lineFieldMap ?? {}),
  };
  const lineRows = cbpExtractLineRows(ticket);
  const orderedHtsCodes = cbpExtractOrderedHtsCodes(ticket);
  const maxRows = Math.max(1, opts.maxLineRows ?? lineRows.length);
  const clampedRows = lineRows.slice(0, maxRows);
  const hts33FieldOrder = cbpGetHts33FieldOrder(form);

  hts33FieldOrder.forEach((fieldName) => clearFieldText(fieldName));

  clampedRows.slice(0, 6).forEach((row, idx) => {
    const templateRowNumber = idx + 1;
    const htsCodeBySummaryOrder = orderedHtsCodes[idx] || row.htsusNumber;
    setFieldText(cbpLineField(lineFieldMap.lineNumber, templateRowNumber), row.lineNumber);

    if (hts33FieldOrder[idx]) {
      setFieldText(hts33FieldOrder[idx], htsCodeBySummaryOrder);
    } else {
      setFieldText(
        cbpLineField(lineFieldMap.htsusNumber, templateRowNumber),
        htsCodeBySummaryOrder,
      );
    }

    setFieldText(cbpLineField(lineFieldMap.materialPercent, templateRowNumber), row.materialPercent);
    setFieldText(cbpLineField(lineFieldMap.enteredValue, templateRowNumber), row.enteredValue);
    setFieldText(cbpLineField(lineFieldMap.htsusRate, templateRowNumber), row.htsusRate);
    setFieldText(cbpLineField(lineFieldMap.dutyAmount, templateRowNumber), row.dutyAmount);
    setFieldText(cbpLineField(lineFieldMap.otherFees, templateRowNumber), row.otherFees);
  });

  clampedRows.forEach((row, idx) => {
    const templateRowNumber = idx + 1;
    const htsCodeBySummaryOrder = orderedHtsCodes[idx] || row.htsusNumber;
    setFieldText(cbpLineField(lineFieldMap2.lineNumber, templateRowNumber), row.lineNumber);
    setFieldText(cbpLineField(lineFieldMap2.htsusNumber, templateRowNumber), htsCodeBySummaryOrder);
    setFieldText(cbpLineField(lineFieldMap2.materialPercent, templateRowNumber), row.materialPercent);
    setFieldText(cbpLineField(lineFieldMap2.enteredValue, templateRowNumber), row.enteredValue);
    setFieldText(cbpLineField(lineFieldMap2.htsusRate, templateRowNumber), row.htsusRate);
    setFieldText(cbpLineField(lineFieldMap2.dutyAmount, templateRowNumber), row.dutyAmount);
    setFieldText(cbpLineField(lineFieldMap2.otherFees, templateRowNumber), row.otherFees);
  });

  if (opts.flatten ?? false) {
    form.flatten();
  }

  const bytes = await pdfDoc.save();
  download(bytes, fileName, 'application/pdf');
};

export const listPdfFormFieldNames = async (
  templateUrl = '/templates/CBP_Form_7501.pdf',
): Promise<string[]> => {
  const response = await fetch(templateUrl);
  if (!response.ok) {
    throw new Error(`Unable to load PDF template at "${templateUrl}"`);
  }

  const bytes = await response.arrayBuffer();
  const pdfDoc = await PDFLibDocument.load(bytes);
  return pdfDoc
    .getForm()
    .getFields()
    .map((field: { getName: () => any }) => field.getName());
};
